import { TOOLBOX_ITEMS } from '../constants';
import type { ElementType, ToolboxItem } from '../types';

/** Library glyphs that should match a different catalog icon. */
const ICON_ALIASES: Record<string, string> = {
  '╱': '◠', // Add New Tool library uses slash for 45° curve
};

export function resolveCatalogIcon(icon: string): string {
  return ICON_ALIASES[icon] ?? icon;
}

function isImageIcon(icon: string): boolean {
  return icon.startsWith('data:') || icon.startsWith('http');
}

/**
 * Find the builtin toolbox template that owns this library icon.
 * Placement behavior is driven by that template's `type` + `defaultProperties`,
 * not by the glyph alone.
 */
export function resolveToolboxTemplateByIcon(
  icon: string | null | undefined,
  options?: { preferredElementTypes?: ElementType[] }
): ToolboxItem | null {
  if (!icon || isImageIcon(icon)) return null;

  const key = resolveCatalogIcon(icon);
  const matches = TOOLBOX_ITEMS.filter((item) => item.icon === key);
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;

  const preferred = options?.preferredElementTypes;
  if (preferred?.length) {
    const hit = matches.find((m) => preferred.includes(m.type));
    if (hit) return hit;
  }

  // Ambiguous "─": prefer track over annotation unless annotations were preferred above.
  const rail = matches.find(
    (m) =>
      m.type === 'overhead-rail' ||
      m.type === 'overhead-curve' ||
      m.type === 'cross-rail'
  );
  return rail ?? matches[0]!;
}

/**
 * Copy placement behavior from a catalog icon template while keeping
 * user-configured standard params (speed, capacity, …) as overrides.
 */
export function behaviorFromIconTemplate(
  template: ToolboxItem | null,
  fallbackType: ElementType,
  userProps: Record<string, unknown> = {}
): {
  type: ElementType;
  defaultProperties: Record<string, unknown> | undefined;
  defaultRotation: number | undefined;
} {
  if (!template) {
    return {
      type: fallbackType,
      defaultProperties: Object.keys(userProps).length > 0 ? userProps : undefined,
      defaultRotation: undefined,
    };
  }

  return {
    type: template.type,
    defaultRotation: template.defaultRotation,
    defaultProperties: {
      ...(template.defaultProperties ?? {}),
      ...userProps,
    },
  };
}
